import React, { ComponentType, forwardRef, useContext } from "react";
import { CSS, styled } from "~/application/theme";
import { FormControlContext } from "../../FormControl/FormControlContext";
import { Icon } from "../../Icon";
import { InputBase, InputStandardProps } from "../base/InputBase";
import { InputAdornment } from "../base/InputBase/InputAdornment";
import { Flex } from "~/components/Flex";
import { Text } from "~/components/Text";

export type TextInputProps = InputStandardProps & {
  style?: React.CSSProperties;
  leftIcon?: ComponentType;
  rightIcon?: ComponentType;
  leftAdornment?: React.ReactElement;
  rightAdornment?: React.ReactElement;
  prefix?: string;
  warning?: boolean;
  leftIconCss?: CSS;
  leftIconSize?: "sm" | "md" | "lg";
  dataCy?: string;
};

const TextInputRoot = styled(InputBase);

export const TextInput = forwardRef<React.ElementRef<typeof TextInputRoot>, TextInputProps>(
  (
    {
      leftIcon,
      prefix,
      rightIcon,
      leftAdornment,
      rightAdornment,
      size = "md",
      name: nameProps,
      disabled: disabledProps,
      onBlur: onBlurProps,
      onChange: onChangeProps,
      value: valueProps,
      warning,
      leftIconCss,
      leftIconSize = "md",
      ...props
    },
    forwardedRef,
  ) => {
    const formControl = useContext(FormControlContext);

    const name = nameProps ?? formControl.name;
    const disabled = disabledProps ?? formControl.disabled;
    const onBlur = onBlurProps ?? formControl.onBlur;
    const onChange = onChangeProps ?? formControl.onChange;
    const value = valueProps ?? formControl.value;
    const isDirty = !!formControl.error;

    return (
      <TextInputRoot
        warning={warning}
        type="text"
        leftAdornment={
          leftAdornment ??
          (prefix || leftIcon ? (
            <Flex gap="2" align="center">
              {prefix && <Text fw="600">{prefix}</Text>}
              {leftIcon && (
                <InputAdornment position="start" disablePointerEvents>
                  <Icon as={leftIcon} size={leftIconSize} css={leftIconCss} />
                </InputAdornment>
              )}
            </Flex>
          ) : undefined)
        }
        rightAdornment={
          rightAdornment ??
          (rightIcon && (
            <InputAdornment position="end" disablePointerEvents>
              <Icon as={rightIcon} size={size} />
            </InputAdornment>
          ))
        }
        size={size}
        name={name}
        inputProps={{ ...props }}
        disabled={disabled}
        onBlur={onBlur}
        onChange={onChange}
        value={value}
        data-dirty={isDirty}
        {...props}
        ref={forwardedRef}
      />
    );
  },
);

TextInput.displayName = "TextInput";
